// eslint-disable-next-line max-classes-per-file
import { devWarning } from '@tanium/coreui-utils';
import { ByteFormatters, getBytesFormatters } from './byteFormatters';
import {
  DateFormatterInput,
  DateFormatters,
  getDateFormatters,
  isDateWithTimezoneProps,
  isValidDate,
} from './dateFormatters';
import { getListFormatters, ListFormatters } from './listFormatters';
import { getNumericFormatters, NumericFormatters } from './numericFormatters';
import {
  DurationProps,
  getRelativeTimeFormatters,
  isDurationProps,
  RelativeTimeFormatters,
} from './relativeTimeFormatters';

let numericFormatters: NumericFormatters;
let byteFormatters: ByteFormatters;
let listFormatters: ListFormatters;
let dateFormatters: DateFormatters;
let formattersLocale: string;
let relativeTimeFormatters: RelativeTimeFormatters;

export const setFormattersLocale = (lng: string) => {
  numericFormatters = getNumericFormatters(lng);
  byteFormatters = getBytesFormatters(lng);
  listFormatters = getListFormatters(lng);
  dateFormatters = getDateFormatters(lng);
  relativeTimeFormatters = getRelativeTimeFormatters(lng);
  formattersLocale = lng;
};

class FormattingError extends Error {
  constructor(message: string, public loudMessage: string = message.toUpperCase()) {
    super(message);
  }
}
class FormatValueError extends FormattingError {
  private static formatValueForError(value: unknown) {
    if (typeof value === 'object' && !(value instanceof Date)) {
      return JSON.stringify(value);
    }

    return String(value);
  }

  constructor(preamble: string, value: unknown) {
    const formattedValue = FormatValueError.formatValueForError(value);

    const message = `${preamble} ${formattedValue}`;
    const loudMessage = `${preamble.toUpperCase()} ${formattedValue}`;

    super(message, loudMessage);
  }
}
class FormatValueTypeError extends FormatValueError {
  constructor(expectedType: string, value: unknown) {
    const a = /^[aeiou]/i.test(expectedType) ? 'an' : 'a';
    const preamble = `Can't apply ${a} ${expectedType} format to`;
    super(preamble, value);
  }
}

const assertNumber: TypeAssertionFunction<number> = (value) => {
  if (typeof value !== 'number') {
    throw new FormatValueTypeError('numeric', value);
  }
};

const assertDate: TypeAssertionFunction<Date> = (value) => {
  if (!isValidDate(value)) {
    throw new FormatValueTypeError('date', value);
  }
};
const assertStringArray: TypeAssertionFunction<string[]> = (value) => {
  if (!Array.isArray(value)) {
    throw new FormatValueTypeError('array', value);
  }
};

const assertDateProps: TypeAssertionFunction<DateFormatterInput> = (value) => {
  if (!isDateWithTimezoneProps(value) && !isValidDate(value)) {
    throw new FormatValueTypeError('date', value);
  }
};

const assertDuration: TypeAssertionFunction<DurationProps> = (value) => {
  if (!isDurationProps(value)) {
    throw new FormatValueTypeError('duration', value);
  }
};

type TypeAssertionFunction<T> = (value: unknown) => asserts value is T;
type FormatterMap<T> = Partial<Record<string, (value: T) => string>>;

// This function is "unsafe" because it throws an error if there's a problem with the supplied value and/or format.
const unsafeFormatter = (value: unknown, format: string | undefined, lng: string): string => {
  if (lng !== formattersLocale) {
    setFormattersLocale(lng);
  }

  const numericFormatterMap: FormatterMap<number> = {
    // Example: 123.456 => '123'
    INTEGER: numericFormatters.formatInteger,
    // Example: 123.456 => '123.5'
    DECIMAL_ONE_DIGIT_MAX: numericFormatters.formatDecimalOneDigitMax,
    // Example: 123 => '123.0' or 123.456 => '123.5'
    DECIMAL_ONE_DIGIT_FIXED: numericFormatters.formatDecimalOneDigitFixed,
    // Example: 123.456 => '123.46'
    DECIMAL_TWO_DIGITS_MAX: numericFormatters.formatDecimalTwoDigitsMax,
    // Example: 1234567 => '1.2M'
    COMPACT_NUMBER: numericFormatters.formatCompact,
    // Example: 1234.56789 => '1,234.568 ms'
    MILLISECONDS: numericFormatters.formatMilliseconds,
    // Example: 0.123456789 => '12%'
    PERCENT_INTEGER: numericFormatters.formatPercentInteger,
    PERCENT: numericFormatters.formatPercentInteger, // Deprecated
    // Example: .87123456789 => '87%', 12.87123456789 => '1K%'
    PERCENT_COMPACT: numericFormatters.formatPercentCompact,
    // Example: .87123456789 => '87.1%', 12.87123456789 => '1.3K%'
    PERCENT_DECIMAL_ONE_DIGIT_MAX_COMPACT: numericFormatters.formatPercentDecimalOneDigitMaxCompact,
    // Example: 0.123456789 => '12.35%'
    PERCENT_DECIMAL_TWO_DIGITS_MAX: numericFormatters.formatPercentDecimalTwoDigitsMax,
    // Example: 150.123456789 => '15.01K%', not likely to show, but if user error results in
    // a bad percentage, we still need this formatter for fitting the value into smaller spaces
    PERCENT_DECIMAL_TWO_DIGITS_MAX_COMPACT:
      numericFormatters.formatPercentDecimalTwoDigitsMaxCompact,
    // Example: 1234.56789 => '1 kB'
    BYTES_INTEGER: byteFormatters.formatBytesInteger,
    // Example: 1234.56789 => '1.21 kB'
    BYTES_DECIMAL_TWO_DIGITS_MAX: byteFormatters.formatBytesDecimalTwoDigitsMax,
    BYTES: byteFormatters.formatBytesDecimalTwoDigitsMax, // Deprecated
    // Example: 1234.56789 => '1 kb/s'
    BITS_PER_SECOND_INTEGER: byteFormatters.formatBitsPerSecondInteger,
    // Example: 1234.56789 => '1.23 kb/s'
    BITS_PER_SECOND_DECIMAL_TWO_DIGITS_MAX: byteFormatters.formatBitsPerSecondDecimalTwoDigitsMax,
    // Example: 1234.56789 => '1 kB/s'
    BYTES_PER_SECOND_INTEGER: byteFormatters.formatBytesPerSecondInteger,
    // Example: 1234.56789 => '1.23 kB/s'
    BYTES_PER_SECOND_DECIMAL_TWO_DIGITS_MAX: byteFormatters.formatBytesPerSecondDecimalTwoDigitsMax,
  };

  const stringArrayFormatterMap: FormatterMap<string[]> = {
    // Example: ['foo', 'bar', 'baz'] => 'foo, bar, baz'
    SHORT_UNIT_LIST: listFormatters.formatShortUnitList,
    // Example: ['foo', 'bar', 'baz'] => 'foo bar baz'
    NARROW_UNIT_LIST: listFormatters.formatNarrowUnitList,
    // Example: ['foo', 'bar', 'baz'] => 'foo, bar, and baz'
    LONG_AND_LIST: listFormatters.formatLongAndList,
  };

  // Date examples all use
  // -- a new Date() taken at March 5, 2021 12:33:08 GMT-0500 (Eastern Standard Time)
  // -- workbenchLocale en-US
  // NOTE: When modifying this map, make a corresponding change within translation-require-valid-formatters.ts
  const dateFormatterMap: FormatterMap<DateFormatterInput> = {
    // Example: '12 PM'
    HOUR_ONLY: dateFormatters.formatHourOnly,
    // Example: '5 PM' Deprecated! use HOUR_ONLY and pass a date and timezone
    HOUR_ONLY_UTC: dateFormatters.formatHourOnlyUTC,
    // Example: '5'
    DAY_ONLY: dateFormatters.formatDayOnly,
    // Example: 'Monday'
    WEEKDAY_ONLY: dateFormatters.formatWeekdayOnly,
    // Example: 'March'
    MONTH_ONLY: dateFormatters.formatMonthOnly,
    // Example: 'March 5'
    MONTH_AND_DAY: dateFormatters.formatMonthAndDay,
    // Example: '2021'
    YEAR_ONLY: dateFormatters.formatYearOnly,
    // Example: '12:33 PM'
    SHORT_TIME: dateFormatters.formatShortTime,
    // Example: '02:33' or '14:33'
    SHORT_TIME_24_HOUR: dateFormatters.formatShortTime24Hour,
    // Example: '3/5/2021'
    SHORT_DATE: dateFormatters.formatShortDate,
    // Example: '3/5/2021' <-- in some cases, is a different date than local date
    // Deprecated! use SHORT_DATE and pass a date and timezone
    SHORT_DATE_UTC: dateFormatters.formatShortDateUTC,
    // Example: 'Monday, 3/5/2021'
    SHORT_DATE_AND_WEEKDAY: dateFormatters.formatShortDateAndWeekday,
    // Example: '3/5/2021, 12:33 PM EST'
    SHORT_DATE_AND_TIME: dateFormatters.formatShortDateAndTime,
    // Example: '3/5/2021, 12:33 PM UTC' Deprecated! use SHORT_DATE_AND_TIME and pass a date and timezone
    SHORT_DATE_AND_TIME_UTC: dateFormatters.formatShortDateAndTimeUTC,
    // Example: '3/5/2021, 12:33:08 PM'
    SHORT_DATE_AND_MEDIUM_TIME: dateFormatters.formatShortDateAndMediumTime,
    // Example: '3/5/2021, 5:33:08 PM' Deprecated! use SHORT_DATE_AND_MEDIUM_TIME and pass a date and timezone
    SHORT_DATE_AND_MEDIUM_TIME_UTC: dateFormatters.formatShortDateAndMediumTimeUTC,
  };

  // NOTE: When modifying this map, make a corresponding change within translation-require-valid-formatters.ts
  const relativeDateFormatters: FormatterMap<Date> = {
    // Example: '1 day ago'
    RELATIVE_TIME_TO_NOW: relativeTimeFormatters.formatRelativeTime,
  };

  // NOTE: When modifying this map, make a corresponding change within translation-require-valid-formatters.ts
  const durationFormatterMap: FormatterMap<DurationProps> = {
    // Example: {date1, date2} => '1 day'
    // where:
    // -- date1: a new Date() taken at March 5, 2021 12:33:08 GMT-0500 (Eastern Standard Time)
    // -- date2: a new Date() taken at March 6, 2021 12:33:08 GMT-0500 (Eastern Standard Time)
    DURATION: relativeTimeFormatters.formatDuration,
  };

  if (format) {
    const checkFormatterMap = <T>(assert: TypeAssertionFunction<T>, map: FormatterMap<T>) => {
      const formatter = map[format];

      if (formatter) {
        assert(value);
        return formatter(value);
      }

      return undefined;
    };

    const formatted =
      checkFormatterMap(assertNumber, numericFormatterMap) ??
      checkFormatterMap(assertStringArray, stringArrayFormatterMap) ??
      checkFormatterMap(assertDateProps, dateFormatterMap) ??
      checkFormatterMap(assertDate, relativeDateFormatters) ??
      checkFormatterMap(assertDuration, durationFormatterMap);

    if (typeof formatted === 'string') return formatted;

    throw new FormattingError(`The format '${format}' does not exist`);
  }

  if (isValidDate(value))
    throw new FormatValueError('Interpolated dates must have a format specified:', value);
  if (typeof value === 'number')
    throw new FormatValueError('Interpolated numbers must have a format specified:', value);
  if (typeof value !== 'string')
    throw new FormatValueError(
      'Only strings can be interpolated when there is no format specified. Received:',
      value,
    );

  return value;
};

// This wraps the "unsafe" formatter to fail a little more gracefully.
export const formatter = (
  value: unknown,
  format: string | undefined,
  lng: string | undefined,
): string => {
  if (lng === undefined)
    throw new Error(
      'Current locale must be specified as the language for interpolation formatting',
    );

  try {
    return unsafeFormatter(value, format, lng);
  } catch (error: unknown) {
    if (process.env.NODE_ENV === 'production') return '';

    devWarning(false, error instanceof Error ? error.message : error);

    const formattingError: FormattingError = (() => {
      if (error instanceof FormattingError) return error;

      return format
        ? new FormatValueError(`An error occurred while applying the "${format}" format to`, value)
        : new FormatValueError('An error occurred while formatting', value);
    })();

    return `🛑🚫⛔️ BROKEN TRANSLATION: ${formattingError.loudMessage} ⛔️🚫🛑`;
  }
};
